/**
 * Questo modulo gestisce la generazione di report Excel per i catalog orders ARShades.
 * 
 * Funzionamento:
 * 1. Riceve un array di catalog orders come input
 * 2. Per ogni catalog order, recupera tutti i prodotti e le varianti associate dal database
 * 3. Calcola le statistiche per ogni catalog order:
 *    - Numero totale di SKU
 *    - SKU pubblicati
 *    - SKU in pubblicazione
 *    - SKU in revisione cliente
 * 4. Genera un file Excel con:
 *    - Una riga di intestazione
 *    - Una riga per ogni catalog order con le relative statistiche
 *    - Formattazione automatica delle colonne
 * 5. Il file viene automaticamente scaricato con il nome "ARShadesReport_[data].xlsx"
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../data/base';
import * as XLSX from 'xlsx';

const generateExcelReport = async (catalogOrders) => {
    if (!XLSX) {
        throw new Error('XLSX library not loaded');
    }

    if (!catalogOrders || !Array.isArray(catalogOrders) || catalogOrders.length === 0) {
        throw new Error('Invalid catalog orders data');
    }

    try {
        // Array per memorizzare i dati del report
        const reportData = [
            ['Catalog Order', 'Total SKU', 'Published SKU', 'In Publication SKU', 'Client Rev. SKU']
        ];

        // Per ogni catalog order, recupera tutti i prodotti e le varianti associate
        for (const catalogOrder of catalogOrders) {
            // 1. Prima trova tutti i prodotti collegati a questo catalog order
            const productsQuery = query(
                collection(db, "Products"),
                where("catalogOrdersRef", "==", catalogOrder.id)
            );
            const productsSnapshot = await getDocs(productsQuery);
            const products = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            let allVariants = [];

            if (products.length > 0) {
                // 2. Per ogni prodotto, trova le varianti associate
                const productRefs = products.map(product => product.id);

                // Gestisci il limite di 10 elementi in Firebase per le query "in"
                const chunks = [];
                for (let i = 0; i < productRefs.length; i += 10) {
                    chunks.push(productRefs.slice(i, i + 10));
                }

                // 3. Recupera tutte le varianti per tutti i chunks
                for (const chunk of chunks) {
                    const variantsQuery = query(
                        collection(db, "Variants"),
                        where("productRef", "in", chunk)
                    );
                    const variantsSnapshot = await getDocs(variantsQuery);
                    const variants = variantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    allVariants = allVariants.concat(variants);
                }
            }

            // 4. Calcola i conteggi
            const totalSku = allVariants.length;
            const skuPubblicati = allVariants.filter(v => v.status === "Pubblicato").length;
            const skuInPubblicazione = allVariants.filter(v => v.status === "In Pubblicazione").length;
            const skuRevCliente = allVariants.filter(v => v.status === "Rev. Cliente").length;

            // 5. Aggiungi la riga per questo catalog order
            reportData.push([
                catalogOrder.displayName || catalogOrder.orderName || 'Undefined',
                totalSku,
                skuPubblicati,
                skuInPubblicazione,
                skuRevCliente
            ]);
        }

        // Crea il workbook con un foglio
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(reportData);

        // Imposta la larghezza delle colonne
        const wscols = [
            { wch: 30 }, // Catalog Order
            { wch: 15 }, // Total SKU
            { wch: 15 }, // Published SKU
            { wch: 20 }, // In Publication SKU
            { wch: 15 }, // Client Rev. SKU
        ];

        ws['!cols'] = wscols;

        // Nome del foglio e del file
        const currentDate = new Date().toISOString().split('T')[0];
        const sheetName = `CatalogOrdersReport_${currentDate}`;
        const fileName = `${sheetName}.xlsx`;

        // Aggiungi il foglio al workbook e scarica
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, fileName);

        return true;
    } catch (error) {
        console.error('Error in generateExcelReport:', error);
        throw error;
    }
};

export { generateExcelReport };